import React, { useContext } from 'react';
import { download, errorHandler } from "../utils";
import { SnackbarContext } from "context/SnackbarContext";

const useDownload = (api, methodName, fileName = undefined) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { setSnackBarConfig } = useContext(SnackbarContext);

  // Helper to parse JSON from Blob with success and error callbacks
  const parseJsonFromBlob = (blob, onSuccess, onError) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        onSuccess(parsed);
      } catch {
        onError();
      }
    };
    reader.readAsText(blob);
  };

  /** --------------------------
   * SUCCESS HANDLERS
   * -------------------------- */
  const handle202Response = (response) => {
    parseJsonFromBlob(
      response.data,
      ({ message }) => {
        setSnackBarConfig({
          open: true,
          message: message || "Report generation has been initiated, Please contact administrator for MBM Grid Report ID.",
          severity: 'info',
        });
      },
      () => {
        setSnackBarConfig({
          open: true,
          message: "We couldn’t process the report data from the server. Please try again later or contact support if the issue continues.",
          severity: 'error',
        });
      }
    );
  };

  const handleJsonResponse = (response) => {
    parseJsonFromBlob(
      response.data,
      ({ message }) => {
        setSnackBarConfig({
          open: true,
          message: message || "Server returned an unexpected response.",
          severity: message?.includes("already being generated") ? 'info' : 'error',
        });
      },
      () => {
        setSnackBarConfig({
          open: true,
          message: "We couldn’t process the report data from the server. Please try again later or contact support if the issue continues.",
          severity: 'error',
        });
      }
    );
  };

  const handleFileDownload = (response, file) => {
    download(response, file || fileName);
    const name = (file || fileName).replace('.xlsx', '');
    const msgName = name.toLowerCase().includes("report") ? name.replace('Report', '') : name;
    setSnackBarConfig({
      open: true,
      message: `${msgName} report download succeeded.`,
      severity: 'success',
    });
  };

  /** --------------------------
   * ERROR HANDLERS
   * -------------------------- */
  const handle429Error = () => {
    setSnackBarConfig({
      open: true,
      message: "The same report is being processed by other users. Please try again in a few moments.",
      severity: 'warning',
    });
  };

  const parseTextFromResponseData = (data) =>
    new Promise((resolve) => {
      if (typeof data === "string") {
        resolve(data);
        return;
      }
      if (data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsText(data);
        return;
      }
      resolve(data?.message || null);
    });

  const handle400Error = async (response) => {
    const message = await parseTextFromResponseData(response?.data);
    setSnackBarConfig({
      open: true,
      message: message || "Bad request. Please check your input.",
      severity: 'error',
    });
  };

  const handle4xxGenericError = async (response) => {
    const message = await parseTextFromResponseData(response?.data);
    setSnackBarConfig({
      open: true,
      message: message || `Error ${response?.status || ''}: Request could not be completed.`,
      severity: 'error',
    });
  };

  const handle5xxError = () => {
    setSnackBarConfig({
      open: true,
      message: "Server error occurred. Please try again later.",
      severity: 'error',
    });
  };

  /** --------------------------
   * MAIN DOWNLOAD LOGIC
   * -------------------------- */
  const handleDownload = async (fileOption, file, payload = null) => {
    setIsDownloading(true);
    try {
      const response = await api[methodName](fileOption, payload);
      const statusCode = response?.status;
      const contentType = (response?.headers?.['content-type'] || '').toLowerCase();

      if (statusCode === 202) {
        handle202Response(response);
        return;
      }

      if (contentType.includes('application/json')) {
        handleJsonResponse(response);
        return;
      }

      handleFileDownload(response, file);

    } catch (error) {
      const statusCode = error.response?.status;
      if (statusCode === 429) {
        handle429Error();
      } else if (statusCode === 400) {
        await handle400Error(error.response);
      } else if (statusCode >= 400 && statusCode < 500) {
        await handle4xxGenericError(error.response);
      } else if (statusCode >= 500) {
        handle5xxError();
      } else {
        errorHandler(error);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return [handleDownload, isDownloading, setIsDownloading];
};

export default useDownload;
